import { ErpDiv, ErpSpan, ErpInputRaw, ErpButtonRaw } from "@/components/erp";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { iconFor } from '@/lib/iconRegistry';
import { Search } from 'lucide-react';

export interface CommandItem {
  id: string;
  label: string;
  icon?: string;
  shortcut?: string;
  section?: string;
  perform: () => void;
}

export interface CommandPaletteProps {
  commands?: CommandItem[];
  placeholder?: string;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  initialQuery?: string;
}

const DEFAULT_COMMANDS_FACTORY = (
  navigate: (path: string) => void,
  t: (k: string) => string,
): CommandItem[] => [
  {
    id: 'go-dashboard',
    label: t('Go_Dashboard') || 'Go to Dashboard',
    icon: 'fas fa-home',
    shortcut: 'G D',
    section: 'Navigation',
    perform: () => navigate('/app/dashboard'),
  },
  {
    id: 'go-sales',
    label: t('Go_Sales') || 'Go to Sales',
    icon: 'fas fa-shopping-cart',
    shortcut: 'G S',
    section: 'Navigation',
    perform: () => navigate('/app/sales'),
  },
  {
    id: 'go-purchases',
    label: t('Go_Purchases') || 'Go to Purchases',
    icon: 'fas fa-truck',
    shortcut: 'G P',
    section: 'Navigation',
    perform: () => navigate('/app/purchases'),
  },
  {
    id: 'go-products',
    label: t('Go_Products') || 'Go to Products',
    icon: 'fas fa-box',
    shortcut: 'G PR',
    section: 'Navigation',
    perform: () => navigate('/app/products'),
  },
  {
    id: 'new-sale',
    label: t('New_Sale') || 'New Sale',
    icon: 'fas fa-plus',
    section: 'Actions',
    perform: () => navigate('/app/sales?new=1'),
  },
  {
    id: 'new-purchase',
    label: t('New_Purchase') || 'New Purchase',
    icon: 'fas fa-plus',
    section: 'Actions',
    perform: () => navigate('/app/purchases?new=1'),
  },
  {
    id: 'open-pos',
    label: t('Open_POS') || 'Open POS Terminal',
    icon: 'fas fa-cash-register',
    shortcut: 'P O S',
    section: 'Actions',
    perform: () => navigate('/app/pos'),
  },
];

export const CommandPalette: React.FC<CommandPaletteProps> = ({
  commands,
  placeholder,
  isOpen: isOpenProp,
  onOpenChange,
  initialQuery,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [internalOpen, setInternalOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  const isOpen = isOpenProp !== undefined ? isOpenProp : internalOpen;

  const setOpen = useCallback((v: boolean) => {
    if (onOpenChange) onOpenChange(v);
    if (isOpenProp === undefined) setInternalOpen(v);
    if (!v) {
      setQuery('');
      setActiveIndex(0);
    }
  }, [onOpenChange, isOpenProp]);

  // Only seed the query on the open transition: re-running this whenever
  // initialQuery changes would wipe what the user is typing.
  const wasOpenRef = useRef(isOpen);
  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;
    wasOpenRef.current = isOpen;
    if (justOpened && initialQuery) {
      setQuery(initialQuery);
    }
  }, [isOpen, initialQuery]);

  const allCommands = useMemo<CommandItem[]>(
    () => commands || DEFAULT_COMMANDS_FACTORY((path: string) => navigate(path), t),
    [commands, navigate, t],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allCommands;
    return allCommands.filter((c) => {
      return (
        c.label.toLowerCase().includes(q) ||
        (c.section || '').toLowerCase().includes(q) ||
        (c.shortcut || '').toLowerCase().includes(q)
      );
    });
  }, [allCommands, query]);

  useEffect(() => {
    if (activeIndex >= filtered.length) setActiveIndex(0);
  }, [filtered.length, activeIndex]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = (e: KeyboardEvent) => {
      const isToggle =
        (e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K');
      if (isToggle) {
        e.preventDefault();
        setOpen(!isOpen);
        return;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, setOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const focusTimer = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(focusTimer);
  }, [isOpen]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const item = filtered[activeIndex];
      if (item) {
        item.perform();
        setOpen(false);
      }
    }
  };

  useEffect(() => {
    if (!gridRef.current) return;
    const active = gridRef.current.querySelector<HTMLElement>(
      `[data-idx="${activeIndex}"]`,
    );
    active?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  if (!isOpen || typeof document === 'undefined') return null;

  return createPortal(
    <ErpDiv
      className="fixed inset-0 z-[100] bg-background/80 backdrop-blur-sm flex items-start justify-center pt-24 sm:pt-32 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      onClick={() => setOpen(false)}
    >
      <ErpDiv 
        className="relative w-full max-w-3xl rounded-xl border bg-card text-card-foreground shadow-2xl overflow-hidden animate-in fade-in zoom-in-95" 
        onClick={(e) => e.stopPropagation()}
      >
        <ErpDiv className="flex items-center border-b px-3">
          <ErpSpan className="mr-2 flex h-4 w-4 shrink-0 opacity-50">
            <Search size={16} aria-hidden="true" />
          </ErpSpan>
          <ErpInputRaw
            ref={inputRef}
            type="text"
            className="flex h-12 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
            placeholder={placeholder || (t('Search_Commands') || 'Type a command.')}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIndex(0);
            }}
            onKeyDown={handleKeyDown}
            autoComplete="off"
            spellCheck={false}
          />
          <ErpSpan className="text-xs text-muted-foreground border rounded-md px-1.5 py-0.5 bg-muted/50">ESC</ErpSpan>
        </ErpDiv>

        {filtered.length === 0 ? (
          <ErpDiv className="py-6 text-center text-sm text-muted-foreground">
            {t('No_Commands_Found') || 'No matching commands'}
          </ErpDiv>
        ) : (
          <ErpDiv ref={gridRef} className="max-h-[400px] overflow-y-auto overflow-x-hidden p-3 scrollbar-thin scrollbar-thumb-muted" role="listbox">
            <ErpDiv className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
              {filtered.map((cmd, idx) => (
                <ErpButtonRaw
                  key={cmd.id}
                  data-idx={idx}
                  role="option"
                  aria-selected={idx === activeIndex}
                  type="button"
                  className={[
                    'relative flex flex-col items-start justify-start gap-2 w-full cursor-default select-none rounded-lg border p-3 text-sm outline-none text-left transition-colors',
                    idx === activeIndex
                      ? 'bg-accent text-accent-foreground border-accent-foreground/30'
                      : 'border-border hover:bg-accent/50 text-foreground',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onMouseEnter={() => setActiveIndex(idx)}
                  onClick={() => {
                    cmd.perform();
                    setOpen(false);
                  }}
                >
                  <ErpDiv className="flex items-center gap-2 w-full">
                    {cmd.icon && (
                      <ErpSpan className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-indigo-600 shrink-0">
                        {iconFor(cmd.icon, 18)}
                      </ErpSpan>
                    )}
                    <ErpSpan className="flex-1 font-medium leading-tight">{cmd.label}</ErpSpan>
                  </ErpDiv>
                  {cmd.shortcut && (
                    <ErpSpan className="mt-auto text-[10px] tracking-widest text-muted-foreground bg-muted/50 border px-1.5 py-0.5 rounded-md">
                      {cmd.shortcut}
                    </ErpSpan>
                  )}
                </ErpButtonRaw>
              ))}
            </ErpDiv>
          </ErpDiv>
        )}
      </ErpDiv>
    </ErpDiv>,
    document.body,
  );
};

export default CommandPalette;
